import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, MapPin } from "lucide-react";
import { api, fileUrl, formatUSD } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import { FitImage } from "@/components/FitImage";
import { useCart } from "@/context/CartContext";

export const PREFILL_KEY = "fabj_prefill_address";

export default function BusinessReorderPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { addCollection, items } = useCart();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/business/reorder/${token}`).then(r => setData(r.data)).catch(e => setError(e.response?.data?.detail || "This reorder link is no longer valid"));
  }, [token]);

  const reorder = () => {
    const col = data.collection;
    if (!items.some(i => i.collection_id === col.id)) addCollection(col);
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify(data.contact));
    toast.success(`${col.name} set added — enter the new location's address`);
    nav("/cart");
  };

  return (
    <PublicShell>
      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-32 pb-24" data-testid="business-reorder-page">
        {error && (
          <div data-testid="reorder-error">
            <div className="overline text-mute mb-3">Reorder</div>
            <h1 className="font-serif text-4xl text-ink mb-6">{error}</h1>
            <Link to="/business" className="btn-ink">Browse business collections</Link>
          </div>
        )}
        {!error && !data && <p className="text-mute">Loading…</p>}
        {data && (
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <FitImage src={fileUrl(data.collection.cover_image_url)} alt={data.collection.name} className="relative aspect-[4/3] bg-cream-soft overflow-hidden" sizes="(min-width:768px) 40vw, 100vw" />
            <div>
              <div className="overline text-mute mb-3 inline-flex items-center gap-2"><MapPin size={12} /> New location</div>
              <h1 className="font-serif text-4xl text-ink" data-testid="reorder-title">Another “{data.collection.name}” set</h1>
              <p className="text-ink-soft mt-4 leading-relaxed">
                Welcome back{data.contact.first_name ? `, ${data.contact.first_name}` : ""}. Last time you ordered {data.previous_sets} set{data.previous_sets === 1 ? "" : "s"}{data.previous_city ? ` for ${data.previous_city}` : ""}.
                We'll pre-fill your contact details — just enter the new address at checkout.
              </p>
              <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-mute">{data.collection.artworks.length} pieces · {data.collection.print_size} in · {data.collection.material} prints</div>
              <div className="mt-2 font-mono-brand text-3xl text-ink" data-testid="reorder-price">{formatUSD(data.collection.price_usd)} <span className="text-sm text-mute">per set</span></div>
              {(data.collection.volume_tiers || []).length > 0 && (
                <div className="text-xs text-mute mt-2">Ordering for several locations? {data.collection.volume_tiers.map(t => `${t.min_qty}+ sets ${t.percent_off}% off`).join(" · ")} — set the quantity in your cart.</div>
              )}
              <button onClick={reorder} data-testid="reorder-add-to-cart"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 bg-ink text-cream text-[11px] uppercase tracking-[0.18em] hover:bg-ink/85">
                <ShoppingBag size={14} /> Order another set
              </button>
            </div>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
